import React from 'react';

import { useSortable } from '@dnd-kit/sortable';

import { useDraggable } from '@dnd-kit/core';

import { Tooltip } from '@mui/material';

import LockIcon from '@mui/icons-material/Lock';

import { colors } from 'theme/colors';

import { Handle } from '../helpers/Handle';
import { ItemProps } from './types';
import { DraggableItemStyled, ItemStyled, Wrapper } from './styles';

import { SortableItemProps } from '../Layout/types';

export const DraggableItem = ({
  id,
  name,
  disabled,
}: {
  id: string;
  name: string;
  disabled: boolean;
}): React.ReactElement => {
  const { isDragging, setNodeRef, listeners } = useDraggable({
    id,
    data: { type: 'item' },
    disabled,
  });

  return (
    <>
      {disabled ? (
        <Tooltip title="Field already used in this layout" placement="right">
          <DraggableItemStyled {...listeners} ref={setNodeRef} key={id} $disabled={disabled} $isDragging={isDragging}>
            <p id={`disabled-field-${name}`}>{name}</p>
          </DraggableItemStyled>
        </Tooltip>
      ) : (
        <DraggableItemStyled {...listeners} ref={setNodeRef} key={id} $disabled={disabled} $isDragging={isDragging}>
          <p id={`active-field-${name}`}>{name}</p>
        </DraggableItemStyled>
      )}
    </>
  );
};

export function SortableItem({
  disabled,
  id,
  name,
  index,
  isLast,
  required,
  lockMessage,
}: SortableItemProps): React.ReactElement {
  const { setNodeRef, listeners, isDragging, transform } = useSortable({
    id,
    // A field in a fixed section is neither dragged nor dropped onto.
    disabled: { draggable: disabled, droppable: disabled },
  });
  const mountedWhileDragging = isDragging;
  return (
    <Item
      ref={setNodeRef}
      value={name}
      dragging={isDragging}
      handle={false}
      index={index}
      transform={transform}
      fadeIn={mountedWhileDragging}
      listeners={listeners}
      isLast={isLast}
      required={required}
      lockMessage={lockMessage}
      fixed={disabled}
    />
  );
}

export const Item = React.memo(
  React.forwardRef<HTMLLIElement, ItemProps>(
    (
      {
        dragOverlay,
        dragging,
        disabled,
        fadeIn,
        handle,
        index,
        listeners,
        transform,
        value,
        isLast,
        required,
        lockMessage,
        fixed,
        ...props
      },
      ref
    ) => (
      <Wrapper
        $fadeIn={Boolean(fadeIn)}
        $dragOverlay={Boolean(dragOverlay)}
        style={
          {
            '--translate-y': transform ? `${Math.round(transform.y)}px` : undefined,
            '--index': index,
          } as React.CSSProperties
        }
        ref={ref}
      >
        <ItemStyled
          $dragging={Boolean(dragging)}
          $disabled={Boolean(disabled)}
          $dragOverlay={Boolean(dragOverlay)}
          {...(!handle && !fixed ? listeners : undefined)}
          {...props}
          tabIndex={!handle && !fixed ? 0 : undefined}
          $isLast={isLast || false}
        >
          {!fixed && <Handle />}
          <p id={`item-${value}`}>{value}</p>
          {required && (
            <Tooltip
              title={
                lockMessage ??
                'This field is required and cannot be removed from the layout. You can move it to other sections, but it must remain part of the layout.'
              }
              placement="bottom"
            >
              <LockIcon htmlColor={colors.grey[400]} sx={{ width: 14, height: 14, marginLeft: '8px' }} />
            </Tooltip>
          )}
        </ItemStyled>
      </Wrapper>
    )
  )
);
